// lib/clients/acalight.ts
// Client configuration for acalight.gr — Greek B2B lighting wholesaler.
// Phase 1 = Greek-only. Multilingual (EN/BG) deferred to Phase 2.
//
// NOTE: acalight does NOT use the agent chat feature, so the categories /
// concepts / system prompt are minimal stubs that never get invoked at
// runtime (gated by features.agentChat = false).
//
// ⚠️ PLACEHOLDER VALUES marked with `PLACEHOLDER:` need real input from
// acalight's team or extraction from their data before going to staging.

import type { ClientConfig } from './types';

// Stubs — agentChat is disabled for acalight, these are never used at runtime.
const PRODUCT_CATEGORIES: string[] = [];
const PRODUCT_CONCEPTS: string[] = [];

function buildAcalightPrompt(_categories: string[], _concepts: string[]): string {
  // No-op: features.agentChat = false for acalight, so this builder never runs.
  return '';
}

export const acalightConfig: ClientConfig = {
  // ── Identity ──
  clientId: 'acalight',
  storeName: 'acalighting.gr',
  storeDescription: 'a Greek B2B lighting wholesaler',

  // ── Features ──
  features: {
    hybridSearch: false, // No embeddings initially — pure lexical (keep crawl simple + cheap)
    agentChat: false, // Acalight does NOT use the AI chat feature
    addToCart: true, // Real OpenCart cart integration (configured in M4)
    externalPricing: true, // Enabled for staging — calls the relative endpoint below
    // Embedded inside acalight's mobile menu search overlay — the user has
    // already expressed intent to search by the time the widget mounts, so
    // skip the trigger-bar step and open the modal directly.
    autoOpen: true,
  },

  // ── Embedding & Integration ──
  // Staging domain delivered by acalight team 2026-05-12 (https://aca.beta4u.gr/).
  // Add their production domain once they confirm it.
  allowedOrigins: ['https://aca.beta4u.gr'],
  // Not live yet — aca.beta4u.gr is their BETA domain, so all its traffic is demo.
  // ⚠️ AT GO-LIVE, EDITING THIS FILE IS NOT THE SWITCH. This client is already provisioned,
  // so the runtime resolves config from Supabase and only falls back to this file if the DB
  // read throws. The switch is a DIRECT DB WRITE adding the real storefront origin to BOTH
  // clients.allowed_origins (CORS) and clients.production_origins (analytics) — see
  // "Runbook: Set Up a New Client" step 8. Keep aca.beta4u.gr OUT of production_origins so
  // beta testing never counts. Mirror the final value here only to keep the fallback honest.
  productionOrigins: [],
  hostingStrategy: 'script_injection',
  cart: { strategy: 'opencart_global', functionName: 'cart.add' },

  // ── External Pricing (per-customer B2B) ──
  // Per acalight's email: relative URL hit same-origin from the host page,
  // so on aca.beta4u.gr this becomes aca.beta4u.gr/index.php?... and the
  // OpenCart session cookie identifies the logged-in customer automatically.
  // If the endpoint isn't deployed yet, the hook catches the error and
  // ProductCard falls back to RRP from the feed (no breakage).
  externalPricing: {
    endpointUrl: '/index.php?route=search_results/ajax_get_prices',
    requestMethod: 'GET',
    idParam: 'products',
    idSeparator: ',',
    credentialsMode: 'same-origin',
    responseFormat: 'opencart_v1',
    // 10s. This value was 3000 but was NEVER READ until 2026-08-31 — the display
    // fetch hardcoded its own 10s — so 10000 preserves acalight's real, current
    // behaviour rather than changing it. NOT measured against acalight's own host;
    // if their endpoint proves slower or faster, measure and tune this per client.
    timeoutMs: 10000,
  },

  // ── Meilisearch ──
  indexName: 'products_acalight',
  embedderName: 'acalight-openai',
  locale: 'ell',

  // ── Feed ──
  // Acalight team switched from XML to JSON on the staging domain (2026-05-12).
  // The JSON schema is identical to the XML, so the same parser logic applies
  // via parseAcalightJsonFeed (which delegates to the XML field extraction).
  feedUrl: 'https://aca.beta4u.gr/product_feed-el-gr.json',
  productLimit: 12000, // Room for the ~10k active SKUs + buffer
  feedFormat: 'acalight_json',

  // ── Dynamic Facet Fields ──
  // Generated 2026-06-01 (banding + Option C expansion). 183 facets total:
  //
  //   • 93 direct-extract facets (Tier B, shipped 2026-06-01) — high-coverage ≥20p,
  //     ≤90 distinct values per the analysis in /tmp/acalight-keys.txt.
  //
  //   • 4 banded fields (power_band, lumens_band, voltage_band, lamp_count_band)
  //     bucketing 4 previously-dropped numeric/messy keys (Power 252-distinct,
  //     Lumens 449, Voltage 62 messy, Num-Lamps 141). The 5th drop, Οικογένεια
  //     (807 product-series names), stays out — not bandable; surfaces via text
  //     search. See lib/crawler/parsers/acalightXml.ts for band-edge logic and
  //     docs/search-engine/FACET_ARCHITECTURE.md §5 for the pattern.
  //
  //   • 85 low-coverage Option C extras (≥1 product, ≤90 distinct). Mostly
  //     solar/PV specs, batteries, motion sensors, niche feature toggles. Most
  //     surface only on searches that hit those product types — the sidebar
  //     dynamically renders only populated facet groups.
  //
  //   • 1 synthesised facet (warehouses_available, sentinel xmlFilterKey
  //     '__warehouse__' routes to extractAvailableWarehouses). See §6.
  facetFields: [
    // ── Tier B: high-coverage facets (already shipped 2026-06-01) ──
    { field: 'ip_rating', label: 'Στεγανότητα (IP)', xmlFilterKey: 'IP- Δείκτης Προστασίας από Υγρά και Σκόνη', isArray: true }, // 6812p/14d
    { field: 'color', label: 'Χρώμα', xmlFilterKey: 'Χρώμα (κύριο)', isArray: true }, // 6467p/62d
    { field: 'material', label: 'Υλικό', xmlFilterKey: 'Υλικό (κύριο)', isArray: true }, // 6228p/45d
    { field: 'light_type', label: 'Τύπος Φωτιστικού', xmlFilterKey: 'Τύπος Φωτιστικού', isArray: true }, // 4547p/4d
    { field: 'beam_direction', label: 'Δέσμη Φωτισμού', xmlFilterKey: 'Δέσμη Φωτισμού/ Κατεύθυνση', isArray: true }, // 4209p/90d
    { field: 'electrical_class', label: 'Ηλεκτρική Προστασία', xmlFilterKey: 'Ηλεκτρική Προστασία', isArray: true }, // 3604p/5d
    { field: 'light_color', label: 'Χρώμα Φωτός', xmlFilterKey: 'Χρώμα Φωτός', isArray: true }, // 3510p/39d
    { field: 'bulb_socket', label: 'Κάλυκας / Ντουϊ', xmlFilterKey: 'Κάλυκας - Ντουϊ', isArray: true }, // 3283p/38d
    { field: 'material_secondary', label: 'Υλικό (δευτερεύον)', xmlFilterKey: 'Υλικό (δευτερεύον)', isArray: true }, // 2850p/27d
    { field: 'cable_material', label: 'Υλικό Καλωδίου', xmlFilterKey: 'Υλικό Καλωδίου', isArray: true }, // 2765p/8d
    { field: 'color_temp', label: 'Θερμοκρασία (K)', xmlFilterKey: 'Θερμοκρασία Χρώματος (Kelvin)', isArray: true }, // 2709p/30d
    { field: 'cable_color', label: 'Χρώμα Καλωδίου', xmlFilterKey: 'Χρώμα Καλωδίου', isArray: true }, // 2549p/17d
    { field: 'installation', label: 'Εγκατάσταση', xmlFilterKey: 'Εγκατάσταση', isArray: true }, // 2526p/11d
    { field: 'cri', label: 'CRI', xmlFilterKey: 'Δείκτης Χρωματικής Απόδοσης (CRI)', isArray: true }, // 2519p/15d
    { field: 'lifespan_hours', label: 'Διάρκεια Ζωής', xmlFilterKey: 'Διάρκεια Ζωής (Hours)', isArray: true }, // 2234p/13d
    { field: 'led_chip', label: 'Τύπος LED', xmlFilterKey: 'Τύπος LED chip', isArray: true }, // 1974p/8d
    { field: 'warranty', label: 'Εγγύηση', xmlFilterKey: 'Εγγύηση', isArray: true }, // 1727p/8d
    { field: 'color_secondary', label: 'Χρώμα (δευτερεύον)', xmlFilterKey: 'Χρώμα (δευτερεύον)', isArray: true }, // 1632p/26d
    { field: 'energy_class', label: 'Ενεργειακή Κλάση', xmlFilterKey: 'Ενεργειακή Κλάση', isArray: true }, // 1613p/7d
    { field: 'dimmable', label: 'Dimmable', xmlFilterKey: 'Dimmable', isArray: true }, // 1558p/7d
    { field: 'rotatable', label: 'Περιστροφή', xmlFilterKey: 'Περιστροφή βραχίονα/κεφαλής', isArray: true }, // 1539p/2d
    { field: 'led_size', label: 'Μέγεθος LED', xmlFilterKey: 'Μέγεθος (1 λαμπάκι ή LED)', isArray: true }, // 874p/13d
    { field: 'led_features', label: 'Χαρακτηριστικά LED', xmlFilterKey: 'Χαρακτηριστικά για 1 λαμπάκι ή LED', isArray: true }, // 829p/23d
    { field: 'function', label: 'Λειτουργία', xmlFilterKey: 'Λειτουργία', isArray: true }, // 795p/9d
    { field: 'shape', label: 'Σχήμα', xmlFilterKey: 'Σχήμα', isArray: true }, // 792p/10d
    { field: 'bulb_cover_color', label: 'Χρώμα Καλύμματος', xmlFilterKey: 'Χρώμα Καλύμματος Λαμπτήρα', isArray: true }, // 651p/9d
    { field: 'accessory_type', label: 'Τύπος Εξαρτήματος', xmlFilterKey: 'Τύπος Εξαρτήματος', isArray: true }, // 584p/32d
    { field: 'special_function', label: 'Ειδική Λειτουργία', xmlFilterKey: 'Ειδική Λειτουργία', isArray: true }, // 554p/12d
    { field: 'bulb_commercial_type', label: 'Εμπορικός Τύπος', xmlFilterKey: 'Εμπορικός Τύπος Λαμπτήρα', isArray: true }, // 548p/53d
    { field: 'power_input', label: 'Τροφοδοσία', xmlFilterKey: 'Τροφοδοσία Ρεύματος', isArray: true }, // 462p/42d
    { field: 'element_count', label: 'Αρ. Στοιχείων', xmlFilterKey: 'Αριθμός Στοιχείων', isArray: true }, // 429p/13d
    { field: 'power_supply', label: 'Παροχή Ρεύματος', xmlFilterKey: 'Παροχή Ρεύματος', isArray: true }, // 360p/2d
    { field: 'current_amps', label: 'Ένταση (A)', xmlFilterKey: 'Ένταση Ρεύματος', isArray: true }, // 345p/14d
    { field: 'contact_count', label: 'Αρ. Επαφών', xmlFilterKey: 'Αριθμός Επαφών', isArray: true }, // 318p/10d
    { field: 'illuminated', label: 'Φωτιζόμενο', xmlFilterKey: 'Φωτιζόμενο', isArray: true }, // 266p/6d
    { field: 'ik_rating', label: 'IK (Κρούση)', xmlFilterKey: 'IK- Βαθμός Μηχανικής Κρούσης', isArray: true }, // 228p/5d
    { field: 'extendable', label: 'Επέκταση', xmlFilterKey: 'Επέκταση', isArray: true }, // 214p/8d
    { field: 'cut_point', label: 'Σημείο Κοπής', xmlFilterKey: 'Σημείο κοπής', isArray: true }, // 212p/20d
    { field: 'socket_plug_type', label: 'Τύπος Πρίζας', xmlFilterKey: 'Τύπος Πρίζας', isArray: true }, // 204p/16d
    { field: 'socket_plug_count', label: 'Αρ. Πριζών', xmlFilterKey: 'Αριθμός Πριζών', isArray: true }, // 199p/7d
    { field: 'connector_type', label: 'Τύπος Συνδέσμου', xmlFilterKey: 'Τύπος Συνδέσμου', isArray: true }, // 185p/14d
    { field: 'positions', label: 'Αρ. Θέσεων', xmlFilterKey: 'Αριθμός Θέσεων', isArray: true }, // 166p/5d
    { field: 'switch_type', label: 'Τύπος Διακόπτη', xmlFilterKey: 'Τύπος Διακόπτη', isArray: true }, // 160p/9d
    { field: 'output_voltage', label: 'Τάση Εξόδου', xmlFilterKey: 'Τάση Εξόδου', isArray: true }, // 152p/7d
    { field: 'certifications', label: 'Σήμανση', xmlFilterKey: 'Σήμανση', isArray: true }, // 147p/4d
    { field: 'mounting', label: 'Στερέωση', xmlFilterKey: 'Στερέωση', isArray: true }, // 147p/3d
    { field: 'tube_type', label: 'Τύπος Σωλήνα', xmlFilterKey: 'Τύπος Σωλήνα', isArray: true }, // 104p/6d
    { field: 'frequency', label: 'Συχνότητα', xmlFilterKey: 'Συχνότητα', isArray: true }, // 104p/5d
    { field: 'wiring_type', label: 'Συνδεσμολογία', xmlFilterKey: 'Συνδεσμολογία', isArray: true }, // 97p/4d
    { field: 'light_transmission', label: 'Μετάδοση Φωτός', xmlFilterKey: 'Μετάδοση Φωτισμού', isArray: true }, // 95p/4d
    { field: 'product_kind', label: 'Είδος Προϊόντος', xmlFilterKey: 'Είδος Προϊόντος', isArray: true }, // 86p/9d
    { field: 'row_count', label: 'Αρ. Σειρών', xmlFilterKey: 'Αριθμός Σειρών', isArray: true }, // 77p/5d
    { field: 'panel_positions', label: 'Αρ. Θέσεων Πίνακα', xmlFilterKey: 'Αριθμός Θέσεων (Πίνακα)', isArray: true }, // 74p/19d
    { field: 'has_cover', label: 'Με Καπάκι', xmlFilterKey: 'Με Καπάκι', isArray: true }, // 67p/2d
    { field: 'motion_range', label: 'Εμβέλεια Κίνησης', xmlFilterKey: 'Εμβέλεια Ανίχνευσης Κίνησης', isArray: true }, // 66p/15d
    { field: 'cable_type', label: 'Τύπος Καλωδίου', xmlFilterKey: 'Τύπος καλωδίου', isArray: true }, // 63p/5d
    { field: 'fused', label: 'Με Ασφάλεια', xmlFilterKey: 'Με Ασφάλεια', isArray: true }, // 63p/2d
    { field: 'button_type', label: 'Τύπος Μπουτόν', xmlFilterKey: 'Τύπος Μπουτόν', isArray: true }, // 62p/6d
    { field: 'channel_type', label: 'Τύπος Καναλιού', xmlFilterKey: 'Τύπος Καναλιού', isArray: true }, // 61p/3d
    { field: 'surge_protection', label: 'Αντικεραυνική Προστασία', xmlFilterKey: 'Αντικεραυνική Προστασία', isArray: true }, // 57p/7d
    { field: 'cable_category', label: 'Κατηγορία Καλωδίου', xmlFilterKey: 'Κατηγορία Καλωδίου', isArray: true }, // 54p/6d
    { field: 'plug_type', label: 'Βύσμα', xmlFilterKey: 'Βύσμα', isArray: true }, // 54p/3d
    { field: 'power_cable_length', label: 'Μήκος Καλωδίου Τροφ.', xmlFilterKey: 'Μήκος Καλωδίου Τροφοδοσίας', isArray: true }, // 52p/3d
    { field: 'load_type', label: 'Τύπος Φορτίου', xmlFilterKey: 'Τύπος Φορτίου', isArray: true }, // 50p/4d
    { field: 'dimmer_tech', label: 'Τεχνολογία Dimmer', xmlFilterKey: 'Τεχνολογία Λειτουργίας Dimmer', isArray: true }, // 49p/5d
    { field: 'compression_resistance', label: 'Αντοχή Συμπίεσης', xmlFilterKey: 'Αντοχή στη Συμπίεση', isArray: true }, // 48p/3d
    { field: 'backup_runtime', label: 'Αυτονομία', xmlFilterKey: 'Αυτονομία/ Εφεδρεία', isArray: true }, // 43p/8d
    { field: 'impact_resistance', label: 'Αντοχή Κρούσης', xmlFilterKey: 'Αντοχή στην Κρούση', isArray: true }, // 39p/3d
    { field: 'max_output_power', label: 'Μέγ. Ισχύς Εξόδου', xmlFilterKey: 'Μέγιστη Ισχύς Εξόδου', isArray: true }, // 37p/19d
    { field: 'wifi_range', label: 'Εμβέλεια WiFi', xmlFilterKey: 'Εμβέλεια Ανίχνευσης WiFi', isArray: true }, // 35p/3d
    { field: 'signal_loss_db', label: 'Απώλεια Σήματος (dB)', xmlFilterKey: 'Απώλεια Σήματος (dB)', isArray: true }, // 35p/3d
    { field: 'certifications_list', label: 'Πιστοποιήσεις', xmlFilterKey: 'Πιστοποιήσεις', isArray: true }, // 33p/2d
    { field: 'blade_count', label: 'Αρ. Πτερυγίων', xmlFilterKey: 'Αριθμός Πτερυγίων', isArray: true }, // 32p/6d
    { field: 'cable_length', label: 'Μήκος Καλωδίου', xmlFilterKey: 'Μήκος Καλωδίου', isArray: true }, // 31p/10d
    { field: 'motor_spec', label: 'Μοτέρ', xmlFilterKey: 'Τύπος & Ονομαστική Ισχύς Μοτέρ', isArray: true }, // 29p/12d
    { field: 'rpm', label: 'Στροφές / Λεπτό', xmlFilterKey: 'Στροφές/Λεπτό', isArray: true }, // 29p/9d
    { field: 'blade_color', label: 'Χρώμα Πτερυγίων', xmlFilterKey: 'Χρώμα Πτερυγίων', isArray: true }, // 29p/7d
    { field: 'blade_material', label: 'Υλικό Πτερυγίων', xmlFilterKey: 'Υλικό Κατασκευής Πτερυγίων', isArray: true }, // 29p/7d
    { field: 'noise_level', label: 'Στάθμη Θορύβου', xmlFilterKey: 'Στάθμη Θορύβου', isArray: true }, // 29p/6d
    { field: 'coverage_area', label: 'Κάλυψη Χώρου', xmlFilterKey: 'Κάλυψη Χώρου', isArray: true }, // 29p/5d
    { field: 'speed_count', label: 'Ταχύτητες', xmlFilterKey: 'Ταχύτητες', isArray: true }, // 29p/3d
    { field: 'max_output_current', label: 'Μέγ. Ένταση Εξόδου', xmlFilterKey: 'Μέγιστη Ένταση Εξόδου', isArray: true }, // 28p/15d
    { field: 'operating_temp', label: 'Θερμοκρασία Λειτουργίας', xmlFilterKey: 'Θερμοκρασία Λειτουργίας', isArray: true }, // 27p/4d
    { field: 'has_switch', label: 'Διακόπτης', xmlFilterKey: 'Διακόπτης', isArray: true }, // 26p/2d
    { field: 'effective_range', label: 'Εμβέλεια', xmlFilterKey: 'Εμβέλεια', isArray: true }, // 25p/4d
    { field: 'transformer_type', label: 'Τύπος Μετασχηματιστή', xmlFilterKey: 'Τύπος Μετασχηματιστή', isArray: true }, // 25p/2d
    { field: 'music', label: 'Μουσική', xmlFilterKey: 'Μουσική', isArray: true }, // 23p/1d
    { field: 'transformer_type_2', label: 'Τύπος Μετασχηματιστή 2', xmlFilterKey: 'Τύπος Μετασχηματιστή 2', isArray: true }, // 22p/5d
    { field: 'timer_hours', label: 'Χρονοδιακόπτης (h)', xmlFilterKey: "Χρονοδιακόπτης &  'Ωρες Λειτουργίας Χρονοδιακόπτη", isArray: true }, // 21p/9d
    { field: 'tube_light_type', label: 'Τύπος Φωτοσωλήνα', xmlFilterKey: 'Τύπος Φωτοσωλήνα', isArray: true }, // 21p/2d
    { field: 'control_mode', label: 'Τρόπος Χειρισμού', xmlFilterKey: 'Τρόπος Χειρισμού', isArray: true }, // 21p/2d
    { field: 'battery_features', label: 'Χαρακτηριστικά Μπαταρίας', xmlFilterKey: 'Χαρακτηριστικά μπαταρίας', isArray: true }, // 20p/12d
    { field: 'diameter', label: 'Διάμετρος', xmlFilterKey: 'Διάμετρος', isArray: true }, // 20p/6d
    // ── Banded high-cardinality fields (2026-06-01 banding) ──
    { field: 'power_band',      label: 'Ισχύς',           xmlFilterKey: 'Ισχύς', isArray: true },                  // 4377p (252 raw → 6 buckets)
    { field: 'lumens_band',     label: 'Φωτεινή Ροή',     xmlFilterKey: 'Φωτεινή Ροή (Lumen)', isArray: true },    // 2642p (449 raw → 6 buckets)
    { field: 'voltage_band',    label: 'Τάση',            xmlFilterKey: 'Τάση (Volt)', isArray: true },            // 6304p (62 raw → 5 buckets)
    { field: 'lamp_count_band', label: 'Αριθμός Λαμπτήρων', xmlFilterKey: 'Αριθμός Λαμπτήρων', isArray: true },    // 3422p (141 raw → 5 buckets)
    // ── Option C extras (≥1 product, ≤90 distinct, non-shipped, non-banded) ──
    { field: 'pv_panel_type', label: 'Τύπος Φωτοβολταϊκού Πάνελ', xmlFilterKey: 'Τύπος Φωτοβολταϊκού Πάνελ', isArray: true }, // 18p/3d
    { field: 'ambient_light_level', label: 'Περιβάλλων φωτισμός', xmlFilterKey: 'Περιβάλλων φωτισμός', isArray: true }, // 18p/5d
    { field: 'battery_autonomy', label: 'Αυτονομία Μπαταρίας', xmlFilterKey: 'Αυτονομία Μπαταρίας', isArray: true }, // 16p/5d
    { field: 'charge_controller', label: 'Ρυθμιστής Φόρτισης', xmlFilterKey: 'Ρυθμιστής Φόρτισης', isArray: true }, // 16p/3d
    { field: 'pv_imp', label: 'Μέγιστο Ονομαστικό Ρεύμα (Imp)', xmlFilterKey: 'Μέγιστο Ονομαστικό Ρεύμα (Imp)', isArray: true }, // 16p/15d
    { field: 'pv_vmp', label: 'Μέγιστη Ονομαστική Τάση (Vmp)', xmlFilterKey: 'Μέγιστη Ονομαστική Τάση (Vmp)', isArray: true }, // 16p/13d
    { field: 'pv_voc', label: 'Τάση ανοιχτοκύκλωσης (Voc)', xmlFilterKey: 'Τάση ανοιχτοκύκλωσης (Voc)', isArray: true }, // 16p/14d
    { field: 'pv_isc', label: 'Ρεύμα Βραχυκύκλωσης (Isc)', xmlFilterKey: 'Ρεύμα Βραχυκύκλωσης (Isc)', isArray: true }, // 16p/15d
    { field: 'detection_angle', label: 'Γωνία Ανίχνευσης', xmlFilterKey: 'Γωνία Ανίχνευσης', isArray: true }, // 15p/4d
    { field: 'nominal_load_value', label: 'Ονομ.Τιμή Φορτίου', xmlFilterKey: 'Ονομ.Τιμή Φορτίου', isArray: true }, // 15p/4d
    { field: 'capacitance_uf', label: 'Χωρητικότητα (μFarad)', xmlFilterKey: 'Χωρητικότητα (μFarad)', isArray: true }, // 14p/13d
    { field: 'test_button', label: 'Κουμπί Δοκιμής', xmlFilterKey: 'Κουμπί Δοκιμής', isArray: true }, // 13p/2d
    { field: 'time_adjustment', label: 'Ρύθμιση χρόνου', xmlFilterKey: 'Ρύθμιση χρόνου', isArray: true }, // 13p/4d
    { field: 'frequency_range', label: 'Εύρος Συχνοτήτων', xmlFilterKey: 'Εύρος Συχνοτήτων', isArray: true }, // 13p/2d
    { field: 'charge_time_solar', label: 'Χρόνος φόρτισης με ηλιακό πάνελ', xmlFilterKey: 'Χρόνος φόρτισης με ηλιακό πάνελ', isArray: true }, // 12p/2d
    { field: 'temp_coef_pm', label: 'Συντελεστής επίδρασης θερμοκρασίας αPm', xmlFilterKey: 'Συντελεστής επίδρασης θερμοκρασίας αPm', isArray: true }, // 12p/4d
    { field: 'temp_coef_voc', label: 'Συντελεστής επίδρασης θερμοκρασίας αVoc', xmlFilterKey: 'Συντελεστής επίδρασης θερμοκρασίας αVoc', isArray: true }, // 12p/4d
    { field: 'temp_coef_isc', label: 'Συντελεστής επίδρασης θερμοκρασίας αIsc', xmlFilterKey: 'Συντελεστής επίδρασης θερμοκρασίας αIsc', isArray: true }, // 12p/4d
    { field: 'battery_type', label: 'Τύπος Μπαταρίας', xmlFilterKey: 'Τύπος Μπαταρίας', isArray: true }, // 11p/3d
    { field: 'led_power', label: 'Ισχύς LED', xmlFilterKey: 'Ισχύς LED', isArray: true }, // 11p/3d
    { field: 'discharge_depth', label: 'Βάθος εκφόρτισης (%)', xmlFilterKey: 'Βάθος εκφόρτισης (%)', isArray: true }, // 11p/1d
    { field: 'terminal_type', label: 'Ακροδέκτες τύπου', xmlFilterKey: 'Ακροδέκτες τύπου', isArray: true }, // 11p/5d
    { field: 'capacity_c20', label: 'Χωρητικότητα C20 (Ah/20HR)', xmlFilterKey: 'Χωρητικότητα C20 (Ah/20HR)', isArray: true }, // 11p/11d
    { field: 'led_strip_width', label: 'Πλάτος Λεντοταινίας', xmlFilterKey: 'Πλάτος Λεντοταινίας', isArray: true }, // 10p/3d
    { field: 'battery_capacity_ah', label: 'Χωρητικότητα Μπαταρίας (Ah)', xmlFilterKey: 'Χωρητικότητα Μπαταρίας (Ah)', isArray: true }, // 10p/10d
    { field: 'connection_1', label: 'Σύνδεση 1', xmlFilterKey: 'Σύνδεση 1', isArray: true }, // 10p/3d
    { field: 'connection_2', label: 'Σύνδεση 2', xmlFilterKey: 'Σύνδεση 2', isArray: true }, // 10p/2d
    { field: 'max_power_coiled', label: 'Μέγιστη ισχύς με καλώδιο τυλιγμένο', xmlFilterKey: 'Μέγιστη ισχύς με καλώδιο τυλιγμένο', isArray: true }, // 10p/2d
    { field: 'max_power_extended', label: 'Μέγιστη ισχύς με καλώδιο σε πλήρη έκταση', xmlFilterKey: 'Μέγιστη ισχύς με καλώδιο σε πλήρη έκταση', isArray: true }, // 10p/2d
    { field: 'performance_warranty', label: 'Εγγύηση απόδοσης', xmlFilterKey: 'Εγγύηση απόδοσης', isArray: true }, // 10p/2d
    { field: 'charging_current', label: 'Ρεύμα Φόρτισης', xmlFilterKey: 'Ρεύμα Φόρτισης', isArray: true }, // 10p/2d
    { field: 'charging_port', label: 'Θύρα Φόρτισης', xmlFilterKey: 'Θύρα Φόρτισης', isArray: true }, // 10p/1d
    { field: 'protection_leakage', label: 'Προστασία Διαρροής', xmlFilterKey: 'Προστασία Διαρροής', isArray: true }, // 10p/1d
    { field: 'integrated_cable', label: 'Ενσωματωμένο Καλώδιο', xmlFilterKey: 'Ενσωματωμένο Καλώδιο', isArray: true }, // 10p/2d
    { field: 'protection_over_under_v', label: 'Προστασία Υπέρτασης & Υπότασης', xmlFilterKey: 'Προστασία Υπέρτασης & Υπότασης', isArray: true }, // 10p/1d
    { field: 'control_method', label: 'Έλεγχος', xmlFilterKey: 'Έλεγχος', isArray: true }, // 10p/2d
    { field: 'protection_ground', label: 'Προστασία Γείωσης', xmlFilterKey: 'Προστασία Γείωσης', isArray: true }, // 10p/1d
    { field: 'protection_supply_line', label: 'Προστασία Γραμμής Τροφοδοσίας', xmlFilterKey: 'Προστασία Γραμμής Τροφοδοσίας', isArray: true }, // 10p/1d
    { field: 'protection_short_circuit', label: 'Προστασία Βραχυκυκλώματος', xmlFilterKey: 'Προστασία Βραχυκυκλώματος', isArray: true }, // 10p/1d
    { field: 'protection_overheat', label: 'Προστασία Υπερθέρμανσης', xmlFilterKey: 'Προστασία Υπερθέρμανσης', isArray: true }, // 10p/1d
    { field: 'charging_power', label: 'Ισχύς Φόρτισης', xmlFilterKey: 'Ισχύς Φόρτισης', isArray: true }, // 10p/4d
    { field: 'protection_overcurrent', label: 'Προστασία Υπερφόρτισης', xmlFilterKey: 'Προστασία Υπερφόρτισης', isArray: true }, // 10p/1d
    { field: 'adapter_type', label: 'Τύπος Αντάπτορα', xmlFilterKey: 'Τύπος Αντάπτορα', isArray: true }, // 9p/3d
    { field: 'bridge_type', label: 'Τύπος Γέφυρας', xmlFilterKey: 'Τύπος Γέφυρας', isArray: true }, // 9p/2d
    { field: 'charge_time_usb', label: 'Χρόνος φόρτισης με USB', xmlFilterKey: 'Χρόνος φόρτισης με USB', isArray: true }, // 9p/2d
    { field: 'connectivity', label: 'Συνδεσιμότητα', xmlFilterKey: 'Συνδεσιμότητα', isArray: true }, // 9p/2d
    { field: 'ceiling_suspension', label: 'Ανάρτηση (Μόνο για Οροφής)', xmlFilterKey: 'Ανάρτηση (Μόνο για Οροφής)', isArray: true }, // 8p/2d
    { field: 'display', label: 'Οθόνη', xmlFilterKey: 'Οθόνη', isArray: true }, // 8p/3d
    { field: 'max_efficiency', label: 'Μέγιστη Απόδοση', xmlFilterKey: 'Μέγιστη Απόδοση', isArray: true }, // 7p/3d
    { field: 'max_power', label: 'Μέγιστη Ισχύς', xmlFilterKey: 'Μέγιστη Ισχύς', isArray: true }, // 7p/4d
    { field: 'junction_box', label: 'Κουτί διασύνδεσης', xmlFilterKey: 'Κουτί διασύνδεσης', isArray: true }, // 7p/2d
    { field: 'pv_array_max_power', label: 'Μέγιστη Ισχύς Συστοιχίας Φ/Β', xmlFilterKey: 'Μέγιστη Ισχύς Συστοιχίας Φ/Β', isArray: true }, // 6p/6d
    { field: 'mppt_voltage_range', label: 'Εύρος MPPT Τάσης Λειτουργίας', xmlFilterKey: 'Εύρος MPPT Τάσης Λειτουργίας', isArray: true }, // 6p/4d
    { field: 'mains_input_range', label: 'Εύρος Τάσης Εισόδου από Δίκτυο', xmlFilterKey: 'Εύρος Τάσης Εισόδου από Δίκτυο', isArray: true }, // 6p/1d
    { field: 'max_charge_current', label: 'Μέγιστο Ρεύμα Φόρτισης', xmlFilterKey: 'Μέγιστο Ρεύμα Φόρτισης', isArray: true }, // 6p/4d
    { field: 'placement', label: 'Τοποθετηση', xmlFilterKey: 'Τοποθετηση', isArray: true }, // 6p/3d
    { field: 'pv_array_max_voc', label: 'Μέγιστη Τάση Ανοιχτού Κυκλώματος Φ/Β Συσ', xmlFilterKey: 'Μέγιστη Τάση Ανοιχτού Κυκλώματος Φ/Β Συστοιχίας', isArray: true }, // 5p/3d
    { field: 'lighting_modes', label: 'Λειτουργίες Φωτισμού', xmlFilterKey: 'Λειτουργίες Φωτισμού', isArray: true }, // 5p/5d
    { field: 'pv_input_voltage', label: 'Τάση Εισόδου από φ/β πάνελ', xmlFilterKey: 'Τάση Εισόδου από φ/β πάνελ', isArray: true }, // 5p/2d
    { field: 'mains_input_voltage', label: 'Τάση Εισόδου από Δίκτυο', xmlFilterKey: 'Τάση Εισόδου από Δίκτυο', isArray: true }, // 5p/1d
    { field: 'sound_db', label: 'Ένταση Ήχου (dB)', xmlFilterKey: 'Ένταση Ήχου (dB)', isArray: true }, // 5p/3d
    { field: 'transformer_category', label: 'Κατηγορία Μετασχηματιστή', xmlFilterKey: 'Κατηγορία Μετασχηματιστή', isArray: true }, // 5p/1d
    { field: 'alarm_intensity', label: 'Ένταη Συναγερμού', xmlFilterKey: 'Ένταη Συναγερμού', isArray: true }, // 3p/2d
    { field: 'module_arrangement', label: 'Διάταξη ενότητας', xmlFilterKey: 'Διάταξη ενότητας', isArray: true }, // 3p/1d
    { field: 'temp_setting_range', label: 'Εύρος Ρύθμισης Θερμοκρασίας', xmlFilterKey: 'Εύρος Ρύθμισης Θερμοκρασίας', isArray: true }, // 2p/2d
    { field: 'thermostat_type', label: 'Τύπος Θερμοστάτη', xmlFilterKey: 'Τύπος Θερμοστάτη', isArray: true }, // 2p/1d
    { field: 'application', label: 'Εφαρμογή', xmlFilterKey: 'Εφαρμογή', isArray: true }, // 2p/2d
    { field: 'operating_humidity', label: 'Υγρασία Λειτουργίας', xmlFilterKey: 'Υγρασία Λειτουργίας', isArray: true }, // 2p/1d
    { field: 'battery_chars_2', label: 'Χαρακτηριστικά Μπαταρίας', xmlFilterKey: 'Χαρακτηριστικά Μπαταρίας', isArray: true }, // 2p/1d
    { field: 'charging_mode', label: 'Τρόπος Φόρτισης', xmlFilterKey: 'Τρόπος Φόρτισης', isArray: true }, // 2p/2d
    { field: 'operation_mode', label: 'Τρόπος Λειτουργίας', xmlFilterKey: 'Τρόπος Λειτουργίας', isArray: true }, // 2p/2d
    { field: 'battery_dod', label: 'Βάθος Εκφόρτισης Μπαταρίας (DoD)', xmlFilterKey: 'Βάθος Εκφόρτισης Μπαταρίας (DoD)', isArray: true }, // 2p/1d
    { field: 'battery_life_cycles', label: 'Κύκλοι Ζωής Μπαταρίας', xmlFilterKey: 'Κύκλοι Ζωής Μπαταρίας', isArray: true }, // 2p/1d
    { field: 'nominal_ac_output_power', label: 'Ονομαστική A.C. Ισχύς Εξόδου', xmlFilterKey: 'Ονομαστική A.C. Ισχύς Εξόδου', isArray: true }, // 1p/1d
    { field: 'ac_grid_freq_range', label: 'AC Εύρος Συχνότητας Δικτύου', xmlFilterKey: 'AC Εύρος Συχνότητας Δικτύου', isArray: true }, // 1p/1d
    { field: 'mppt_short_circuit_current', label: 'Ρεύμα βραχυχκυκλώσεως / MPPT', xmlFilterKey: 'Ρεύμα βραχυχκυκλώσεως / MPPT', isArray: true }, // 1p/1d
    { field: 'dimensions', label: 'Διαστάσεις ΜxΥxΠ', xmlFilterKey: 'Διαστάσεις ΜxΥxΠ', isArray: true }, // 1p/1d
    { field: 'max_output_current_a', label: 'Μεγ. Ρεύμα Εξόδου (A)', xmlFilterKey: 'Μεγ. Ρεύμα Εξόδου (A)', isArray: true }, // 1p/1d
    { field: 'communication', label: 'Επικοινωνία', xmlFilterKey: 'Επικοινωνία', isArray: true }, // 1p/1d
    { field: 'weight', label: 'Βάρος', xmlFilterKey: 'Βάρος', isArray: true }, // 1p/1d
    { field: 'max_input_current_mppt', label: 'Μεγ. Ρεύμα εισόδου / MPPT', xmlFilterKey: 'Μεγ. Ρεύμα εισόδου / MPPT', isArray: true }, // 1p/1d
    { field: 'mppt_count', label: 'Αριθμός MPPT', xmlFilterKey: 'Αριθμός MPPT', isArray: true }, // 1p/1d
    { field: 'startup_voltage', label: 'Τάση Εκκίνησης', xmlFilterKey: 'Τάση Εκκίνησης', isArray: true }, // 1p/1d
    { field: 'light_power', label: 'Ισχύς φωτός', xmlFilterKey: 'Ισχύς φωτός', isArray: true }, // 1p/1d
    { field: 'sound_options', label: 'Επιλογές Ήχου', xmlFilterKey: 'Επιλογές Ήχου', isArray: true }, // 1p/1d
    // ── Synthesised warehouse facet ──
    { field: 'warehouses_available', label: 'Διαθεσιμότητα Αποθήκης', xmlFilterKey: '__warehouse__', isArray: true },
  ],

  // ── Search Tuning ──
  search: {
    semanticRatio: 0.0, // Pure lexical — no embeddings configured (avoids OpenAI cost)
    minScoreThreshold: 0.65,
    fallbackScoreThreshold: 0.75,
    maxResultsToFetch: 1000,
    maxResultsToReturn: 1000,
  },

  // ── Agent Search Tuning ──
  agentSearch: {
    minScoreThreshold: 0.75,
    layer4ScoreThreshold: 0.7,
    layer1: { limit: 100, matchingStrategy: 'last' },
    layer2: { maxCategories: 15, totalBudget: 80, minPerCategory: 12, matchingStrategy: 'last' },
    layer3: { limit: 20, maxKeywords: 5, minKeywordLength: 4, matchingStrategy: 'last' },
    layer4: { limit: 120, matchingStrategy: 'all' },
    discovery: { minTermLength: 4, matchingStrategy: 'last' },
    maxProductsToReturn: 200,
  },

  // ── Language ──
  language: {
    stopWords: [
      'ο', 'η', 'το', 'τα', 'του', 'τις', 'τον',
      'κε', 'για', 'σε', 'με', 'απο', 'προς',
    ],
    // PLACEHOLDER: synonyms should come from real query data or a domain
    // expert at acalight. Empty = no synonyms applied. Add as we learn.
    synonyms: {},
    queryStopWords: [
      'από', 'απο', 'με', 'για', 'σε', 'και', 'στο', 'στη', 'στα',
      'στον', 'στην', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
      'ο', 'η', 'το', 'οι', 'τα', 'ένα', 'ενα', 'μια', 'μία', 'προς',
    ],
    pricePatterns: {
      maxPrice: /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|up to|under|below)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      minPrice: /(?:πάνω από|πανω απο|πανω|πάνω|από|απο|over|above)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      range: /(?:από|απο|from)?\s*(\d+(?:\.\d+)?)\s*(?:-|–|έως|εως|μέχρι|μεχρι|to)\s*(\d+(?:\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
    },
    noisyPhrases: [],
  },

  // ── Index Settings ──
  indexSettings: {
    searchableAttributes: [
      'title', 'title_normalized',
      'brand',
      'model', // Critical for B2B — buyers often search by SKU code
      'category', 'category_normalized',
      'id',
      'description_short', 'description_short_normalized',
    ],
    // Display ALL document attributes. With 90+ dynamic facet fields it's not
    // practical to keep an explicit whitelist in sync, and Meilisearch's `'*'`
    // wildcard means "everything indexed on the document" — including all
    // facet field arrays needed by the sidebar filter chips.
    //
    // Side effect: the `*_normalized` helper fields (title_normalized, etc.)
    // are also returned in search responses. They're small strings — not a
    // bandwidth concern for B2B browsing — and we already pay to index them
    // (Greek phonetic-search). If we ever need to trim them out we can
    // switch back to an explicit list.
    displayedAttributes: ['*'],
    rankingRules: [
      'words', 'typo', 'proximity', 'attribute', 'sort', 'exactness',
    ],
    localizedAttributes: [
      {
        locales: ['ell'],
        attributePatterns: ['title', 'category', 'description_short'],
      },
    ],
    documentTemplate:
      '{{doc.category}} - {{doc.brand}} {{doc.model}} {{doc.title}}. {{doc.description}}',
    faceting: {
      maxValuesPerFacet: 500,
      sortFacetValuesBy: { '*': 'count' as const },
    },
    // Pinned to Meilisearch's default (5/9) — which is what this index has
    // ACTUALLY been running all along. This block was never sent to the engine
    // until 2026-08-25 (see applyIndexSettings), so the 4/8 written here
    // had never taken effect. Wiring it up would have silently made this client
    // MORE typo-tolerant than it has ever been, untested. Pinning to 5/9 keeps
    // behaviour byte-identical. Tune it deliberately, with a measured battery,
    // the way syfak's 6/10 was — do not copy syfak's number blindly.
typoTolerance: {
      minWordSizeForTypos: { oneTypo: 5, twoTypos: 9 },
    },
  },

  // ── System Prompt ──
  systemPrompt: {
    categories: PRODUCT_CATEGORIES,
    productConcepts: PRODUCT_CONCEPTS,
    buildPrompt: buildAcalightPrompt,
  },

  // ── UI Configuration ──
  ui: {
    // PLACEHOLDER: brand colors must come from acalight (their actual brand
    // identity, logo, hover variants). Using a clearly placeholder magenta
    // here so it's visually obvious during dev that branding hasn't been
    // applied yet.
    // Extracted from acalight's dynamic-theme.css `--primary-color` on 2026-05-12.
    // Hover darkens roughly 10% (the site's CSS has darker variants #cb1a17 / #b51715
    // / #9e1412 — we use the lightest darker for hover).
    brandColor: '#e21d1a',
    brandColorHover: '#cb1a17',
    scrollbarColor: '#e21d1a',
    // PLACEHOLDER: font — confirm with acalight whether to use Gotham Greek
    // (currently shared with parousiasi) or a different family.
    fontCssVariable: '--font-gotham-greek',
    // PLACEHOLDER: review acalight's full category tree to decide what to
    // hide. The top-level "ΠΡΟΙΟΝΤΑ" is the wrapper of everything in the
    // feed — definitely hide it. Anything else TBD.
    categoriesToHide: new Set([
      'ΠΡΟΙΟΝΤΑ',
    ]),
    // PLACEHOLDER: example queries should reflect what acalight's actual
    // B2B buyers search for. Leaving empty until acalight gives us examples
    // or we have real query analytics.
    exampleQueries: [],
    // PLACEHOLDER: top searches — sensible defaults for a B2B lighting catalog.
    // Should be replaced with real query analytics or acalight's curated list
    // once we have either.
    topSearches: [
      'led προβολεας',
      'φωτιστικο οροφης',
      'λαμπες led',
      'spot',
      'led panel',
    ],
    strings: {
      // PLACEHOLDER: search placeholder rotation — acalight should review.
      searchPlaceholder: [
        'Αναζήτηση προϊόντων...',
      ],
      brandLabel: 'Brand',
      filtersTitle: 'Φίλτρα',
      suggestedCategories: 'Προτεινόμενες Κατηγορίες',
      otherCategories: 'Άλλες Κατηγορίες',
      priceRange: 'Εύρος Τιμής',
      productsTitle: 'Προϊόντα',
      noResults: 'Δε βρέθηκαν προϊόντα',
      noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις-κλειδιά ή πιο γενικούς όρους',
      showLess: 'Λιγότερα',
      showMore: (n: number) => `+${n} ακόμα...`,
      popularProducts: 'Δημοφιλή Προϊόντα',
      searchingSuggestions: 'Αναζήτηση Προτάσεων',
      searchingProducts: 'Αναζήτηση προϊόντων...',
      addToCart: 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ',
      topSearchesTitle: 'Top Αναζητήσεις',
      // Agent chat strings — never shown since features.agentChat = false.
      aiAssistantTitle: '',
      aiAssistantGreeting: '',
    },
  },
};
